import { Shift, ShiftReactionStatus, ShiftStatus, TeacherShift } from 'types/collections';

/**
 * Centralized shift filtering utilities to ensure consistency across the app
 */

/**
 * Teacher shift filtering utilities
 */
export class TeacherShiftFilters {
  /**
   * Check if a teacher can still apply to a shift (hasn't reacted yet)
   */
  static canApplyToShift(
    shift: Shift, 
    reactions: TeacherShift[] | undefined
  ): boolean {
    // Only open shifts can be applied to
    if (shift.status !== ShiftStatus.OPEN) return false;

    const reaction = reactions?.find(r => r.id === shift.id);
    
    // If reactions are loaded but shift not found, it was filtered out (refused/rejected)
    if (reactions && reactions.length > 0 && !reaction) {
      return false;
    }

    // Can only apply if no reaction or not_reacted status
    return !reaction || reaction.reaction_status === ShiftReactionStatus.NOT_REACTED;
  }

  /**
   * Check if a school has any shifts the teacher can apply to
   */
  static hasAvailableShifts(
    schoolShifts: Shift[] | undefined,
    reactions: TeacherShift[] | undefined
  ): boolean {
    if (!schoolShifts?.length) return false;

    return schoolShifts.some(shift => this.canApplyToShift(shift, reactions));
  }

  /**
   * Filter shifts for individual listing view (only show NOT_REACTED)
   */
  static filterListingShifts(
    shifts: Shift[] | undefined,
    reactions: TeacherShift[] | undefined
  ): Shift[] {
    if (!shifts) return [];

    return shifts.filter(shift => {
      // Only show open shifts
      if (shift.status !== ShiftStatus.OPEN) return false;

      const reaction = reactions?.find(r => r.id === shift.id);

      // If no reaction found, show the shift (teacher can apply)
      if (!reaction) return true;

      // Only show shifts where teacher hasn't reacted yet
      return reaction.reaction_status === ShiftReactionStatus.NOT_REACTED;
    });
  }

  /**
   * Filter shifts for teacher dashboard (show relevant workflow states)
   */
  static filterDashboardShifts(
    shifts: any[] | undefined,
    reactions: Array<{ id: string; reaction_status: ShiftReactionStatus }> | undefined
  ): any[] {
    if (!shifts) return [];

    const reactionMap = new Map(reactions?.map(r => [r.id, r.reaction_status]) || []);

    return shifts.filter(shift => {
      const reaction = reactionMap.get(shift.id);

      // Show OPEN shifts where teacher hasn't reacted or applied
      if (shift.status === ShiftStatus.OPEN && 
          (!reaction || 
           reaction === ShiftReactionStatus.NOT_REACTED || 
           reaction === ShiftReactionStatus.APPLIED)) {
        return true;
      }

      // Show shifts where teacher was rejected (for notification)
      if (reaction === ShiftReactionStatus.REJECTED) {
        return true;
      }

      // Show FILLED shifts where teacher was approved
      if (shift.status === ShiftStatus.FILLED && reaction === ShiftReactionStatus.APPROVED) {
        return true;
      }

      // Hide refused shifts
      return reaction !== ShiftReactionStatus.REFUSED;
    });
  }

  /**
   * Sort schools by availability (schools with available shifts first)
   */
  static sortSchoolsByAvailability<T extends { id: string; shifts?: Shift[] }>(
    schools: T[],
    reactions: TeacherShift[] | undefined
  ): T[] {
    return [...schools].sort((a, b) => {
      const aHasShifts = this.hasAvailableShifts(a.shifts, reactions);
      const bHasShifts = this.hasAvailableShifts(b.shifts, reactions);

      // Schools with available shifts come first
      if (aHasShifts && !bHasShifts) return -1;
      if (!aHasShifts && bHasShifts) return 1;
      return 0;
    });
  }
} 